import { apiFetch } from "./client";

/**
 * Kontrolne tačke — where a sample is taken, and what is measured there.
 *
 * Carried over from the sampling groups of Bankom's in-house lab program, where a point was a name and a
 * list of tests and everything else lived inside the name. Here the plant, the machine and the kind of
 * material are fields, and the head of quality edits them: she configures the tests, so she configures
 * the places too, on the same screen.
 */

export const CONTROL_POINT_KINDS = ["RAW", "SEMI_PRODUCT", "PRODUCT", "UTILITY"] as const;
export type ControlPointKind = (typeof CONTROL_POINT_KINDS)[number];

export type ControlPointParameter = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  decimals: number;
  resultType: string | null;
  discipline: string;
};

export type ControlPoint = {
  id: string;
  code: string;
  nameI18n: Record<string, string> | null;
  kind: ControlPointKind;
  productionStepId: string | null;
  productionStepCode: string | null;
  productionStepName: Record<string, string> | null;
  equipmentCode: string | null;
  legacyGroupId: number | null;
  /** The group name exactly as the lab program holds it. Shown, not hidden: the lab recognises it. */
  legacyName: string | null;
  note: string | null;
  sortOrder: number;
  active: boolean;
  /** How many archived analyses hang off the point. Tells a point in use from one only entered. */
  analyses: number;
  parameters: ControlPointParameter[];
};

export type CreateControlPointBody = {
  code: string;
  nameI18n?: Record<string, string>;
  kind: ControlPointKind;
  productionStepId?: string | null;
  equipmentCode?: string | null;
  note?: string | null;
  sortOrder?: number;
  parameterIds?: string[];
};

export type UpdateControlPointBody = {
  nameI18n?: Record<string, string>;
  kind?: ControlPointKind;
  productionStepId?: string | null;
  /** True clears the step. A plain null would be indistinguishable from "leave it alone". */
  clearProductionStep?: boolean;
  equipmentCode?: string | null;
  note?: string | null;
  sortOrder?: number;
  active?: boolean;
};

export const controlPointApi = {
  list: (activeOnly = true) =>
    apiFetch<ControlPoint[]>(`/quality/control-points?activeOnly=${activeOnly}`),
  create: (body: CreateControlPointBody) =>
    apiFetch<ControlPoint>("/quality/control-points", { method: "POST", body }),
  update: (id: string, body: UpdateControlPointBody) =>
    apiFetch<ControlPoint>(`/quality/control-points/${id}`, { method: "PUT", body }),
  setParameters: (id: string, parameterIds: string[]) =>
    apiFetch<ControlPoint>(`/quality/control-points/${id}/parameters`, {
      method: "PUT",
      body: { parameterIds },
    }),
  retire: (id: string) => apiFetch<void>(`/quality/control-points/${id}`, { method: "DELETE" }),
};
